import { FC, Fragment, ReactNode, useRef } from 'react';

import {
    CheckableCard,
    Tag,
    Text,
    TextStyle,
    TooltipHover,
    useBreakpoint,
    VSpacing,
    VSpacingContainer,
} from '@hh.ru/magritte-ui';
import { CheckableCardBorderRadius, CheckableCardPadding } from '@hh.ru/magritte-ui-checkable-card/types';
import { HeadersTypography, TextTypography } from '@hh.ru/magritte-ui-typography/types';
import { QuestionCircleOutlinedSize16 } from '@hh.ru/magritte-ui/icon';

import styles from 'components/AuctionBuilder/BiddingStrategySelect/BiddingStrategyCard/BiddingStrategyCard.module.less';

type Placement = 'builder' | 'multicreation';

const placementConfig: Record<
    Placement,
    {
        padding: CheckableCardPadding;
        borderRadius: CheckableCardBorderRadius;
        tagsPlacement: 'vertical' | 'horizontal';
        labelTypography: TextTypography | HeadersTypography;
        descriptionStyle: TextStyle;
    }
> = {
    builder: {
        padding: 16,
        borderRadius: 16,
        tagsPlacement: 'vertical',
        labelTypography: 'subtitle-1-semibold',
        descriptionStyle: 'secondary',
    },
    multicreation: {
        padding: 24,
        borderRadius: 24,
        tagsPlacement: 'horizontal',
        labelTypography: 'title-4-semibold',
        descriptionStyle: 'primary',
    },
};

interface BiddingStrategyCardProps {
    label: string;
    description: string[];
    checked: boolean;
    onClick: () => void;
    tags?: string[];
    labelTooltipContent?: ReactNode;
    dataQa?: string;
    placement?: Placement;
}

export const BiddingStrategyCard: FC<BiddingStrategyCardProps> = ({
    label,
    description,
    checked,
    onClick,
    tags,
    labelTooltipContent,
    dataQa,
    placement = 'builder',
}) => {
    const { padding, borderRadius, tagsPlacement, labelTypography, descriptionStyle } = placementConfig[placement];
    const activatorRef = useRef<HTMLSpanElement>(null);
    const { isMobile } = useBreakpoint();

    return (
        <>
            <div className={styles.cardWrapper}>
                <CheckableCard
                    flexible
                    padding={padding}
                    borderRadius={borderRadius}
                    toneHover={true}
                    type="radio"
                    name="billing-strategy"
                    checked={checked}
                    onChange={onClick}
                    data-qa={dataQa}
                >
                    <VSpacingContainer default={8}>
                        <div className={styles.label}>
                            <Text typography={labelTypography}>{label}</Text>
                            {labelTooltipContent && !isMobile && (
                                <span ref={activatorRef} tabIndex={0}>
                                    <QuestionCircleOutlinedSize16 initialColor="secondary" />
                                </span>
                            )}
                        </div>
                        {description.map((descriptionItem, index) => (
                            <Fragment key={`${descriptionItem}-${index}`}>
                                <Text typography="label-3-regular" style={descriptionStyle}>
                                    {descriptionItem}
                                </Text>
                            </Fragment>
                        ))}
                        {!!tags?.length && (
                            <>
                                <VSpacing default={0} />
                                <div
                                    className={
                                        tagsPlacement === 'horizontal' ? styles.tagsHorizontal : styles.tagsVertical
                                    }
                                >
                                    {tags.map((tag) => (
                                        <Tag key={tag} style="positive" size="medium">
                                            {tag}
                                        </Tag>
                                    ))}
                                </div>
                            </>
                        )}
                    </VSpacingContainer>
                </CheckableCard>
            </div>
            {labelTooltipContent && !isMobile && (
                <TooltipHover
                    activatorRef={activatorRef}
                    placement="top-center"
                    data-qa="form-label-tooltip"
                    size="medium"
                >
                    {labelTooltipContent}
                </TooltipHover>
            )}
        </>
    );
};
